import React, { useMemo, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeModeProvider } from './contexts/ThemeModeContext';
import { OfflineProvider } from './hooks/useOffline';
import { ProtectedRoute, WhatsAppSupportButton } from './components';
import Layout from './components/common/Layout';
import ChunkErrorBoundary from './components/common/ChunkErrorBoundary';
import GlobalOrderNotifications from './components/orders/GlobalOrderNotifications';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { ROUTES } from './constants';
import { UserRole } from './types';
import { createAppTheme } from './theme';
import { lazyWithRetry } from './utils/lazyWithRetry';

// ── Lazy-loaded pages (code-split with auto-retry on chunk failure) ──
// Auth
const LoginPage = lazyWithRetry(() => import('./pages/auth/LoginPage'));
// Admin
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'));
const RestaurantListPage = lazyWithRetry(() => import('./pages/admin/RestaurantListPage'));
const RestaurantFormPage = lazyWithRetry(() => import('./pages/admin/RestaurantFormPage'));
const RestaurantStatsPage = lazyWithRetry(() => import('./pages/admin/RestaurantStatsPage'));
// Restaurant Owner
const RestaurantDashboard = lazyWithRetry(() => import('./pages/restaurant/RestaurantDashboard'));
const SettingsPage = lazyWithRetry(() => import('./pages/restaurant/SettingsPage'));
const CategoriesPage = lazyWithRetry(() => import('./pages/restaurant/CategoriesPage'));
const DishesPage = lazyWithRetry(() => import('./pages/restaurant/DishesPage'));
const DishFormPage = lazyWithRetry(() => import('./pages/restaurant/DishFormPage'));
const OrdersPage = lazyWithRetry(() => import('./pages/restaurant/OrdersPage'));
const TablesPage = lazyWithRetry(() => import('./pages/restaurant/TablesPage'));
const CashierPage = lazyWithRetry(() => import('./pages/restaurant/CashierPage'));
const InventoryPage = lazyWithRetry(() => import('./pages/restaurant/InventoryPage'));
const RecipeTemplatesPage = lazyWithRetry(() => import('./pages/restaurant/RecipeTemplatesPage'));
const SchedulingPage = lazyWithRetry(() => import('./pages/restaurant/SchedulingPage'));
const StaffPage = lazyWithRetry(() => import('./pages/restaurant/StaffPage'));
const NotificationsPage = lazyWithRetry(() => import('./pages/restaurant/NotificationsPage'));
const LearningCenterPage = lazyWithRetry(() => import('./pages/restaurant/LearningCenterPage'));
const PrinterSettingsPage = lazyWithRetry(() => import('./pages/restaurant/PrinterSettingsPage'));
const PrintServerManagerPage = lazyWithRetry(() => import('./pages/restaurant/PrintServerManagerPage'));
// Accountant / Financial
const AccountantDashboard = lazyWithRetry(() => import('./pages/restaurant/AccountantDashboard'));
const ExpensesPage = lazyWithRetry(() => import('./pages/restaurant/ExpensesPage'));
const ReportsPage = lazyWithRetry(() => import('./pages/restaurant/ReportsPage'));
const CashRegisterPage = lazyWithRetry(() => import('./pages/restaurant/CashRegisterPage'));
const DeliveryCompaniesPage = lazyWithRetry(() => import('./pages/restaurant/DeliveryCompaniesPage'));
const DeliveryOrdersPage = lazyWithRetry(() => import('./pages/restaurant/DeliveryOrdersPage'));
const DeliveryCashierPage = lazyWithRetry(() => import('./pages/restaurant/DeliveryCashierPage'));
// Public
const HomePage = lazyWithRetry(() => import('./pages/public/HomePage'));
const MenuPage = lazyWithRetry(() => import('./pages/public/MenuPage'));
const CartPage = lazyWithRetry(() => import('./pages/public/CartPage'));
const CheckoutPage = lazyWithRetry(() => import('./pages/public/CheckoutPage'));
const OrderSuccessPage = lazyWithRetry(() => import('./pages/public/OrderSuccessPage'));
const TrackOrderPage = lazyWithRetry(() => import('./pages/public/TrackOrderPage'));
const TableSessionPage = lazyWithRetry(() => import('./pages/public/TableSessionPage'));

/** Minimal centered spinner shown while a lazy page chunk is loading */
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress size={36} sx={{ color: '#6366f1' }} />
  </Box>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      networkMode: 'offlineFirst',
      gcTime: Infinity, // Don't garbage-collect cached data — needed for offline
      staleTime: 5 * 60 * 1000, // 5 min — consider data fresh for 5 min
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

const ThemedApp: React.FC = () => {
  const { direction } = useLanguage();
  const theme = useMemo(() => createAppTheme(direction), [direction]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <DocumentMetaManager />
          <CartProvider>
            <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route
                    path={ROUTES.HOME}
                    element={
                      <Layout>
                        <HomePage />
                      </Layout>
                    }
                  />
                  <Route
                    path={ROUTES.MENU}
                    element={<MenuPage />}
                  />
                  {/* QR Code scan landing page — exchanges URL params for a secure table session JWT */}
                  <Route path="/join" element={<TableSessionPage />} />
                  <Route
                    path={ROUTES.LOGIN}
                    element={<LoginPage />}
                  />

                  {/* Cart & Order Routes (Public) */}
                  <Route
                    path={ROUTES.CART}
                    element={<CartPage />}
                  />
                  <Route
                    path={ROUTES.CHECKOUT}
                    element={<CheckoutPage />}
                  />
                  <Route
                    path={ROUTES.ORDER_SUCCESS}
                    element={<OrderSuccessPage />}
                  />
                  <Route
                    path={ROUTES.ORDER_TRACK}
                    element={<TrackOrderPage />}
                  />

                  {/* Admin Routes - Wrapped in AdminLayout */}
                  <Route element={<AdminLayout />}>
                    <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
                    <Route path={ROUTES.ADMIN_RESTAURANTS} element={<RestaurantListPage />} />
                    <Route path={ROUTES.ADMIN_RESTAURANTS_CREATE} element={<RestaurantFormPage />} />
                    <Route path={ROUTES.ADMIN_RESTAURANTS_EDIT} element={<RestaurantFormPage />} />
                    <Route path={ROUTES.ADMIN_RESTAURANT_STATS} element={<RestaurantStatsPage />} />
                    <Route path={ROUTES.ADMIN_RESTAURANTS_ALL_STATS} element={<RestaurantStatsPage />} />
                  </Route>

                  {/* Restaurant Routes — Owner + Staff */}
                  <Route element={<RestaurantLayout />}>
                    {/* Routes that DELIVERY_CASHIER is BLOCKED from accessing */}
                    <Route element={<ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.WAITER, UserRole.ACCOUNTANT]} />}>
                      <Route path={ROUTES.RESTAURANT_DASHBOARD} element={
                        <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER]}>
                          <RestaurantDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path={ROUTES.RESTAURANT_CATEGORIES} element={<CategoriesPage />} />
                      <Route path={ROUTES.RESTAURANT_DISHES} element={<DishesPage />} />
                      <Route path={ROUTES.RESTAURANT_DISHES_CREATE} element={<DishFormPage />} />
                      <Route path={ROUTES.RESTAURANT_DISHES_EDIT} element={<DishFormPage />} />
                      <Route path={ROUTES.RESTAURANT_SCHEDULING} element={<SchedulingPage />} />
                      <Route path={ROUTES.RESTAURANT_ORDERS} element={<OrdersPage />} />
                      <Route path={ROUTES.RESTAURANT_TABLES} element={<TablesPage />} />
                      <Route path={ROUTES.RESTAURANT_STAFF} element={<StaffPage />} />
                      <Route path={ROUTES.RESTAURANT_PRINTER_SETTINGS} element={<PrinterSettingsPage />} />
                      <Route path={ROUTES.RESTAURANT_PRINT_SERVER_MANAGER} element={<PrintServerManagerPage />} />

                      {/* Accountant / Financial (restricted from standard cashier/waiter too) */}
                      <Route element={<ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.ACCOUNTANT]} />}>
                        <Route path={ROUTES.RESTAURANT_ACCOUNTANT} element={<AccountantDashboard />} />
                        <Route path={ROUTES.RESTAURANT_EXPENSES} element={<ExpensesPage />} />
                        <Route path={ROUTES.RESTAURANT_REPORTS} element={<ReportsPage />} />
                        <Route path={ROUTES.RESTAURANT_CASH_REGISTER} element={<CashRegisterPage />} />
                      </Route>
                    </Route>

                    {/* Routes that DELIVERY_CASHIER CAN access */}
                    <Route path={ROUTES.RESTAURANT_CASHIER} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.WAITER, UserRole.ACCOUNTANT, UserRole.DELIVERY_CASHIER]}>
                        <CashierPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_DELIVERY_COMPANIES} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.ACCOUNTANT]}>
                        <DeliveryCompaniesPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_DELIVERY_ORDERS} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.ACCOUNTANT, UserRole.CASHIER, UserRole.DELIVERY_CASHIER]}>
                        <DeliveryOrdersPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_DELIVERY_CASHIER} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.DELIVERY_CASHIER]}>
                        <DeliveryCashierPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_NOTIFICATIONS} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.WAITER, UserRole.ACCOUNTANT, UserRole.DELIVERY_CASHIER, UserRole.INVENTORY_MANAGER]}>
                        <NotificationsPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_INVENTORY} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.ACCOUNTANT, UserRole.INVENTORY_MANAGER]}>
                        <InventoryPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_TEMPLATES} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.WAITER, UserRole.ACCOUNTANT, UserRole.INVENTORY_MANAGER]}>
                        <RecipeTemplatesPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_LEARNING_CENTER} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.ACCOUNTANT, UserRole.DELIVERY_CASHIER, UserRole.INVENTORY_MANAGER]}>
                        <LearningCenterPage />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.RESTAURANT_SETTINGS} element={
                      <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.ACCOUNTANT, UserRole.DELIVERY_CASHIER, UserRole.INVENTORY_MANAGER]}>
                        <SettingsPage />
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* Catch all - redirect to home */}
                  <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
                </Routes>
              </Suspense>
            </ChunkErrorBoundary>

            <WhatsAppSupportButton />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

// Layout Wrappers
const AdminLayout = () => (
  <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
    <Layout showSidebar>
      <Outlet />
    </Layout>
  </ProtectedRoute>
);

const RestaurantLayout = () => (
  <ProtectedRoute allowedRoles={[UserRole.RESTAURANT_OWNER, UserRole.CASHIER, UserRole.WAITER, UserRole.ACCOUNTANT, UserRole.DELIVERY_CASHIER, UserRole.INVENTORY_MANAGER]}>
    <OnboardingProvider>
      <Layout showSidebar>
        <GlobalOrderNotifications />
        <OnboardingWizard />
        <Outlet />
      </Layout>
    </OnboardingProvider>
  </ProtectedRoute>
);

/** Renders nothing — just runs the useDocumentMeta hook inside AuthProvider scope */
const DocumentMetaManager: React.FC = () => {
  useDocumentMeta();
  return null;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <OfflineProvider>
        <ThemeModeProvider>
          <LanguageProvider>
            <ThemedApp />
          </LanguageProvider>
        </ThemeModeProvider>
      </OfflineProvider>
    </QueryClientProvider>
  );
};

export default App;
